/**
 * C0-GEN — deterministic Option-A editorial fallback (Wave 1, Phase 1).
 *
 * `composeEditorialFallback(report, sectionId)` reconstructs an
 * {@link EditorialSectionLayer} from the report's ALREADY-VERIFIED content,
 * purely and deterministically — no LLM, no I/O, no `Date.now`/`Math.random`.
 *
 * It is the byte-identical guarantee behind the editorial feature: the
 * resolver (`resolveEditorialLayer`) returns this fallback whenever a
 * generated `editorialNarrative` layer is absent (which, in Wave 1, is
 * ALWAYS — no generator writes the field yet). The fields it returns mirror
 * exactly the strings the legacy renderers display today:
 *
 *   - `editorialHeadline` ← the registry section label (`getSectionLabel`),
 *      mirroring `defaultSectionHeading` MINUS its call-site literal fallback.
 *      Returned ONLY when the registry knows a label for `sectionId` (i.e.
 *      `getSectionLabel(...) !== sectionId`); otherwise OMITTED so the render
 *      call-site `?? defaultSectionHeading(id, '<literal>')` keeps the exact
 *      current heading. This is what makes the headline route byte-identical.
 *   - `editorialThesis` ← the FIRST verified sentence of the section's prose.
 *   - `editorialNarrative` ← the section's verified prose, UNCHANGED.
 *   - `pullQuote` ← omitted (the deterministic compose never lifts a quote).
 *
 * The headline + label come from the SAME deterministic builders that feed
 * the legacy headings, so wiring the render through the resolver cannot
 * change the rendered text.
 *
 * NB: `editorialThesis` / `editorialNarrative` are part of the fallback API
 * consumed by the grounding validators and the Wave 2/3 render. In Wave 1 the
 * render routes ONLY the headline through the resolver; the prose bodies are
 * untouched (the spec forbids prose restructuring until Wave 3). So even a
 * fully-populated fallback never changes Wave-1 output.
 */
import type { SiteRagReport, EditorialSectionLayer } from '../../types/siteRag';
import { getSectionLabel } from '../../config/reportSections';
import { splitSentences } from '../citationDensity';

/**
 * Deterministic map from a section id to a getter for that section's primary
 * VERIFIED prose string on the report. Sections absent from this map have no
 * single-prose body (e.g. the multi-field grid / transport / connectivity
 * studies), so the fallback returns no narrative/thesis for them — only the
 * registry headline. Keep this in lock-step with the render: every entry's
 * getter must read the SAME field the legacy renderer displays.
 */
const SECTION_PROSE: Record<string, (report: SiteRagReport) => string | undefined> = {
  // The cover standfirst falls back to the executive summary today.
  cover: (r) => r.executiveSummary,
  executiveSummary: (r) => r.executiveSummary,
  executive: (r) => r.executiveSummary,
  projectIntentOverview: (r) => r.projectIntentOverview,
  conclusion: (r) => r.conclusion?.recommendation,
};

/** First non-empty verified sentence of `prose`, or `undefined`. */
function firstSentence(prose: string | undefined): string | undefined {
  if (typeof prose !== 'string') return undefined;
  const sentences = splitSentences(prose);
  return sentences.length > 0 ? sentences[0] : undefined;
}

/**
 * Compose the deterministic Option-A editorial layer for one section.
 * Pure + total: never throws, never returns `undefined` (always an object,
 * with each field present only when the source content exists).
 */
export function composeEditorialFallback(
  report: SiteRagReport,
  sectionId: string,
): EditorialSectionLayer {
  const layer: EditorialSectionLayer = {};

  // Headline — registry label only when known; omit otherwise so the
  // render call-site literal fallback wins (byte-identical).
  const label = getSectionLabel(sectionId, report.reportType);
  if (label && label !== sectionId) {
    layer.editorialHeadline = label;
  }

  // Narrative + thesis from the section's verified prose, UNCHANGED.
  const prose = SECTION_PROSE[sectionId]?.(report)?.trim();
  if (prose) {
    layer.editorialNarrative = prose;
    const thesis = firstSentence(prose);
    if (thesis) layer.editorialThesis = thesis;
  }

  // pullQuote intentionally omitted — the deterministic compose never lifts
  // a quote (a fabricated attribution is unverifiable; see spec fix #5).

  return layer;
}
